class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedResultCounts = {};
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    form.addEventListener('reset', this.onFormReset.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    this.toggleResetButton();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  shouldResetForm() {
    return !this.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();

    if (this.shouldResetForm()) {
      this.close(true);
      this.input.focus();
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;

    const resetIsHidden = this.resetButton.classList.contains('hidden');

    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getQueryKey(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateTotalResultsCount(searchTerm, queryKey);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[options][unavailable_products]')}=show&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;

        if (this.getQueryKey(this.getQuery()) !== queryKey) return;

        this.renderSearchResults(resultsMarkup);
        this.updateTotalResultsCount(searchTerm, queryKey);
      })
      .catch((error) => {
        this.close();
        throw error;
      }); 
  }

  getQueryKey(searchTerm) {
    return searchTerm.trim().replace(/\s+/g, '-').toLowerCase();
  }

  updateTotalResultsCount(searchTerm, queryKey) {
    if (this.cachedResultCounts[queryKey]) {
      this.renderTotalResultsCount(this.cachedResultCounts[queryKey]);
      return;
    }

    this.fetchSearchResultsCount(searchTerm, true)
      .then((countData) => countData || this.fetchSearchResultsCount(searchTerm, false))
      .then((countData) => {
        if (!countData) return;
        this.cachedResultCounts[queryKey] = countData;

        if (this.getQueryKey(this.getQuery()) === queryKey) {
          this.renderTotalResultsCount(countData);
        }
      })
      .catch(() => {
        // Do not show the limited predictive-search count as the total count.
      });
  }

  fetchSearchResultsCount(searchTerm, useSectionEndpoint) {
    return fetch(this.getSearchCountUrl(searchTerm, useSectionEndpoint))
      .then((response) => {
        if (!response.ok) throw new Error(response.status);

        return response.text();
      })
      .then((text) => this.parseSearchResultsCount(text))
      .catch((error) => {
        if (useSectionEndpoint) return null;
        throw error;
      });
  }

  getSearchCountUrl(searchTerm, useSectionEndpoint) {
    const searchCountUrl = new URL(routes.search_url, window.location.origin);
    searchCountUrl.searchParams.set('q', searchTerm);
    searchCountUrl.searchParams.set('options[prefix]', 'last');
    searchCountUrl.searchParams.set('type', 'product');

    if (useSectionEndpoint) searchCountUrl.searchParams.set('section_id', 'search-results-count');

    return searchCountUrl.toString();
  }

  parseSearchResultsCount(text) {
    const countElement = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('[data-search-results-count]');

    if (!countElement) return null;

    const countData = {
      count: Number(countElement.dataset.searchResultsCount),
      text: countElement.textContent.trim(),
    };

    return Number.isFinite(countData.count) && countData.text ? countData : null;
  }

  renderTotalResultsCount(countData) {
    const visibleCount = this.querySelector('[data-predictive-search-results-count]');
    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');

    if (visibleCount) {
      visibleCount.textContent = countData.text;
      visibleCount.classList.toggle('hidden', countData.count === 0);
    }
    if (liveRegionCount) liveRegionCount.textContent = countData.text;
    if (this.statusElement) this.setLiveRegionText(countData.text);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.statusElement) this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  clearLiveRegionText() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;

    this.statusElement.textContent = '';
    this.statusElement.setAttribute('aria-hidden', 'true');
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    if (this.statusElement) {
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }
  } 

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) { 
    if (clearSearchTerm) {
      this.input.value = '';
      this.predictiveSearchResults.innerHTML = '';
      this.removeAttribute('results');
      this.removeAttribute('loading');
      this.clearLiveRegionText();
      this.toggleResetButton();
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
